import React, { useState, useEffect, useCallback } from 'react';
import { Box, InputAdornment, TableRow, IconButton } from '@mui/material';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    PersonOutline as PersonOutlineIcon,
    PersonAddAltOutlined as PersonAddAltOutlinedIcon,
    PersonOffOutlined as PersonOffOutlinedIcon
} from '@mui/icons-material';

import {
    PageHeader, PageTitle, SearchInput,
    StatusTabsContainer, StyledTabs, StatusTab,
    StyledTableContainer, StyledTable, StyledTableBody, StyledTableCell,
    StatusTag, PaginationFooter,
    SummaryCardsContainer, SummaryCard, SummaryIconWrapper, SummaryContent, SummaryTitle, SummaryValue, SummaryDescription,
    WorkerListContainer
} from './styles';

import CustomPagination from '../../../../../../components/CustomPagination';
import CustomTableHeader from '../../../../../../components/CustomHeader';
import { IWorker } from '../../../../../../modules/worker/types';
import Loader from '../../../../../../components/common/Loader';
import apiCall from '../../../../../../utils/api';

type Order = 'asc' | 'desc';

interface Column {
    id: keyof IWorker | string;
    label: string;
    numeric?: boolean;
    disableSorting?: boolean;
}

const COLUMN_MAPPING: { [key: string]: keyof IWorker | string } = {
    'ID No': 'worker_id',
    'Full Name': 'first_name',
    'Email ID': 'user.email',
    'Phone Number': 'user.phone_number',
    'Emergency Contact': 'emergency_contact',
    'Location Name': 'location_name',
    'Status': 'status',
};

const WORKER_COLUMNS: Column[] = Object.entries(COLUMN_MAPPING).map(([label, id]) => ({
    id: id,
    label: label,
    numeric: id === 'worker_id',
    disableSorting: true,
}));

interface WorkerListProps {
    orgId: string | number;
}

const WorkerList: React.FC<WorkerListProps> = ({ orgId }) => {
    const [tabValue, setTabValue] = useState(0);
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<keyof IWorker | string>('worker_id');

    const [workerList, setWorkerList] = useState<IWorker[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalWorkers, setTotalWorkers] = useState(0);
    const [workerCounts, setWorkerCounts] = useState<any>(null);
    const [statsLoading, setStatsLoading] = useState(false);

    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const response = await apiCall<any>('get', `/api/organization/superadmin/detail/${orgId}/`);
            if (response.data && response.data.data) {
                setWorkerCounts({
                    all_workers: response.data.data.worker_count || 0,
                    pending_workers: response.data.data.pending_worker_count || 0,
                    suspended_workers: response.data.data.suspended_worker_count || 0,
                    new_registrations: response.data.data.new_worker_count_30_days || 0,
                    approved_workers: response.data.data.approved_worker_count || 0,
                });
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setStatsLoading(false);
        }
    }, [orgId]);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const trimmedSearchTerm = searchTerm.trim();
            const response = await apiCall<any>('get', `/api/worker/list/?org=${orgId}&page=${page}&search=${trimmedSearchTerm}&status=${status}&ordering=${order === 'desc' ? '-' : ''}${String(orderBy)}`);
            if (response.data && response.data.data) {
                setWorkerList(response.data.data.results || []);
                setTotalWorkers(response.data.data.count || 0);
            }
        } catch (error) {
            console.error('Error fetching worker list:', error);
        } finally {
            setLoading(false);
        }
    }, [orgId, page, searchTerm, status, order, orderBy]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const handleStatusChange = (newStatus: string) => {
        setStatus(newStatus);
        setPage(1);
    }

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: string,
    ) => {
        const propertyKey = property as keyof IWorker | string;
        const isAsc = orderBy === propertyKey && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(propertyKey);
    };

    return (
        <Box>
            {loading && <Loader open={loading} />}
            <SummaryCardsContainer>
                <SummaryCard>
                    <SummaryIconWrapper colorName="primary">
                        <PersonOutlineIcon />
                    </SummaryIconWrapper>
                    <SummaryContent>
                        <SummaryTitle>Total No of Workers</SummaryTitle>
                        <SummaryValue>{statsLoading ? '0' : workerCounts?.all_workers ?? 0}</SummaryValue>
                        <SummaryDescription>{statsLoading ? '0' : workerCounts?.pending_workers ?? 0} onboarding</SummaryDescription>
                    </SummaryContent>
                </SummaryCard>
                <SummaryCard>
                    <SummaryIconWrapper colorName="info">
                        <PersonAddAltOutlinedIcon />
                    </SummaryIconWrapper>
                    <SummaryContent>
                        <SummaryTitle>New Registration</SummaryTitle>
                        <SummaryValue>{statsLoading ? '0' : workerCounts?.new_registrations ?? 0}</SummaryValue>
                        <SummaryDescription>Last 30 Days</SummaryDescription>
                    </SummaryContent>
                </SummaryCard>
                <SummaryCard>
                    <SummaryIconWrapper colorName="error">
                        <PersonOffOutlinedIcon />
                    </SummaryIconWrapper>
                    <SummaryContent>
                        <SummaryTitle>Suspended Workers</SummaryTitle>
                        <SummaryValue>{statsLoading ? '0' : workerCounts?.suspended_workers ?? 0}</SummaryValue>
                        <SummaryDescription>Require Action</SummaryDescription>
                    </SummaryContent>
                </SummaryCard>
            </SummaryCardsContainer>

            <WorkerListContainer>
                <PageHeader>
                    <PageTitle>Workers List</PageTitle>
                    <SearchInput
                        placeholder="Search by Name, ID..."
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(event) => {
                            setSearchTerm(event.target.value as string);
                            setPage(1);
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchTerm && (
                                        <IconButton
                                            aria-label="clear search"
                                            onClick={() => setSearchTerm('')}
                                            edge="end"
                                            size="small"
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    )}
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </PageHeader>


                <StatusTabsContainer>
                    <StyledTabs value={tabValue} onChange={handleTabChange} aria-label="status tabs">
                        <StatusTab
                            label={"All (" + (workerCounts?.all_workers || 0) + ")"}
                            onClick={() => handleStatusChange('')}
                        />
                        <StatusTab
                            label={"Pending (" + (workerCounts?.pending_workers || 0) + ")"}
                            onClick={() => handleStatusChange('pending')}
                        />
                        <StatusTab
                            label={"Suspended (" + (workerCounts?.suspended_workers || 0) + ")"}
                            onClick={() => handleStatusChange('suspended')}
                        />
                        <StatusTab
                            label={"Approved (" + (workerCounts?.approved_workers || 0) + ")"}
                            onClick={() => handleStatusChange('active')}
                        />
                    </StyledTabs>
                </StatusTabsContainer>

                <StyledTableContainer>
                    <StyledTable stickyHeader>
                        <CustomTableHeader
                            columns={WORKER_COLUMNS}
                            order={order}
                            orderBy={orderBy as string}
                            onRequestSort={handleRequestSort}
                        />

                        <StyledTableBody>
                            {!loading && !workerList.length ? (
                                <TableRow hover sx={{ cursor: "default" }}>
                                    <StyledTableCell colSpan={WORKER_COLUMNS.length} align="center">
                                        No data available
                                    </StyledTableCell>
                                </TableRow>
                            ) : (
                                workerList.map((worker) => (
                                    <TableRow
                                        key={worker.worker_id}
                                        hover
                                        sx={{ cursor: "default" }}
                                    >
                                        <StyledTableCell>{worker.worker_id}</StyledTableCell>
                                        <StyledTableCell>{`${worker.user?.first_name} ${worker.user?.last_name}`}</StyledTableCell>
                                        <StyledTableCell>{worker.user?.email}</StyledTableCell>
                                        <StyledTableCell>{worker.user?.phone_number}</StyledTableCell>
                                        <StyledTableCell>{worker.emergency_contact}</StyledTableCell>
                                        <StyledTableCell>{worker.location_name}</StyledTableCell>
                                        <StyledTableCell>
                                            <StatusTag status={worker.status}>
                                                {worker.status.toUpperCase()}
                                            </StatusTag>
                                        </StyledTableCell>
                                    </TableRow>
                                ))
                            )}
                        </StyledTableBody>
                    </StyledTable>
                </StyledTableContainer>
            </WorkerListContainer>

            <PaginationFooter>
                <Box>
                    Showing {workerList?.length || 0} of {totalWorkers} results
                </Box>
                <CustomPagination
                    totalPages={Math.ceil(totalWorkers / rowsPerPage)}
                    currentPage={page}
                    onPageChange={(newPage: number) => setPage(newPage)}
                />
            </PaginationFooter>
        </Box>
    );
};

export default WorkerList;
